/**
 * Opens header nav dropdowns (snippets/header-dropdown-menu.liquid) on
 * mouse hover instead of requiring a click, per request - closes again
 * on mouse-out, with a short grace delay so moving the pointer from the
 * trigger into the flyout submenu doesn't cause it to snap shut in
 * transit. Applies to both the top-level dropdown (e.g. "Products") and
 * the second-level flyouts nested inside it (e.g. "Tarp Systems").
 *
 * Deliberately NOT a CSS-only ":hover { opacity: 1 }" trick, even though
 * that would technically work here (confirmed live: Dawn already keeps
 * .header__submenu at display:block/position:absolute regardless of the
 * <details> open state, showing/hiding it purely via opacity - the usual
 * "native <details> content is display:none when closed" blocker some
 * hover-dropdown write-ups warn about doesn't actually apply to this
 * theme's markup). A pure-CSS reveal would desync the VISUAL state from
 * the real <details open>/aria-expanded state that keyboard/screen-reader
 * users and Dawn's own header-menu custom element (details-disclosure.js)
 * rely on. Toggling the real `open` attribute on hover instead keeps
 * exactly one source of truth for "is this open" - mouse users just get
 * an additional, faster way to set it, on top of the click/keyboard path
 * that already exists and is left completely untouched.
 *
 * Skipped entirely on touch/coarse-pointer devices (media query below) -
 * hover has no meaning there, and the existing tap-to-toggle behavior is
 * what mobile/tablet users expect and already have.
 *
 * Switching straight from one menu to a sibling (e.g. "Products" ->
 * "Brands", or "Tarp Systems" -> "Tarps" within the same flyout) closes
 * the old one IMMEDIATELY rather than waiting out CLOSE_DELAY_MS - per
 * request, that delay made switching menus feel noticeably laggy
 * (both were briefly visible at once). The delay itself stays for the
 * case it's actually needed for: leaving a trigger to cross into its
 * own flyout, where an instant close would snap the menu shut before
 * the pointer arrives.
 */
(function () {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  var CLOSE_DELAY_MS = 250;
  var closeTimers = new WeakMap();

  function closeNow(details) {
    var timer = closeTimers.get(details);
    if (timer) {
      clearTimeout(timer);
      closeTimers.delete(details);
    }
    if (!details.hasAttribute('open')) return;
    details.removeAttribute('open');
    var summary = details.querySelector(':scope > summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
    details.dispatchEvent(new Event('toggle'));
  }

  // Closes whichever OTHER details (if any) is open at the same menu
  // level as this one - siblings share a parent <li>'s parent <ul>,
  // covering both the top-level structure (<li><header-menu><details>)
  // and the flyout level (<li><details>, no header-menu wrapper there).
  function closeOpenSiblings(details) {
    var li = details.closest('li');
    var list = li ? li.parentElement : null;
    if (!list) return;
    Array.from(list.children).forEach(function (siblingLi) {
      if (siblingLi === li) return;
      var sibling = siblingLi.querySelector(':scope > details, :scope > header-menu > details');
      if (sibling && sibling !== details && sibling.hasAttribute('open')) {
        closeNow(sibling);
      }
    });
  }

  function open(details) {
    var timer = closeTimers.get(details);
    if (timer) {
      clearTimeout(timer);
      closeTimers.delete(details);
    }
    closeOpenSiblings(details);
    if (details.hasAttribute('open')) return;
    details.setAttribute('open', '');
    var summary = details.querySelector(':scope > summary');
    if (summary) summary.setAttribute('aria-expanded', 'true');
    // Native <details> fires a real 'toggle' event for attribute
    // changes too, but only in browsers new enough to support it firing
    // synchronously off a direct attribute mutation in every case - Dawn's
    // own header-menu component (details-disclosure.js) listens for
    // 'toggle' to drive the reveal animation and the "keep header visible
    // while a menu is open" logic, so dispatch one explicitly rather than
    // assume the browser already did.
    details.dispatchEvent(new Event('toggle'));
  }

  function scheduleClose(details) {
    if (closeTimers.has(details)) return;
    var timer = setTimeout(function () {
      closeTimers.delete(details);
      closeNow(details);
    }, CLOSE_DELAY_MS);
    closeTimers.set(details, timer);
  }

  function bind(details) {
    if (details.dataset.hoverMenuBound) return;
    details.dataset.hoverMenuBound = 'true';
    details.addEventListener('mouseenter', function () {
      open(details);
    });
    details.addEventListener('mouseleave', function () {
      scheduleClose(details);
    });
  }

  document.querySelectorAll('.header__inline-menu header-menu details').forEach(bind);
})();
